import React, { useEffect, useState, useMemo } from "react";
import { setCurrentPageTitle } from "../../store/slices/pageSlice";
import { useDispatch, useSelector } from "react-redux";
// import AddIcon from "@mui/icons-material/Add";
// import RemoveIcon from "@mui/icons-material/Remove";
import {
    createSalesTax,
    deleteSalesTax,
    fetchSalesTaxes,
    updateSalesTax,
} from "../../store/slices/salesTaxesSlice";
import { useSnackbar } from "notistack";
import DeleteIcon from "@mui/icons-material/Delete";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { MdSearch } from "react-icons/md";
import { fetchEmployees } from "../../store/slices/employeeSlice";

const DEFAULT_TAXES = [
    { taxName: "G&A Expenses", taxPercentage: "15" },
    { taxName: "Tax", taxPercentage: "27" },
];

const FinanceTax = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [searchQuery, setSearchQuery] = useState("");
    const [isModal, setIsModal] = useState(false);
    const [defaultRowToRemove, setDefaultRowToRemove] = useState(null);
    useEffect(() => {
        dispatch(setCurrentPageTitle("Add G&A Tax Rates"));
    }, [dispatch]);

    const { taxes } = useSelector((state) => state.salesTaxes);
    const { employees } = useSelector((state) => state.employees);

    useEffect(() => {
        dispatch(fetchSalesTaxes());
        dispatch(fetchEmployees());

    }, [dispatch]);

    // const [selectedrowid, setselectedrowid] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [rowid, setRowToDelete] = useState(null);

    const [rows, setRows] = useState([]);
    const [allRows, setAllRows] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "ascending",
    });


    // Merge: show defaults + DB taxes; if DB has same name as default, show DB entry only
    useEffect(() => {
        const userEmail = localStorage.getItem("email");
        const currentEmployee = employees.find((emp) => emp.EmpEmail === userEmail);
        const dbTaxes = (taxes || []).filter(
            (tax) => tax.createdBy === currentEmployee?._id
        );
        const dbNamesLower = new Set(
            dbTaxes.map((t) => (t.taxName || "").trim().toLowerCase())
        );

        const merged = [];
        let rowId = Date.now();

        DEFAULT_TAXES.forEach((def) => {
            const nameLower = (def.taxName || "").trim().toLowerCase();
            if (!dbNamesLower.has(nameLower)) {
                merged.push({
                    id: rowId++,
                    taxName: def.taxName,
                    taxPercentage: def.taxPercentage,
                    isNew: true,
                });
            }
        });

        dbTaxes.forEach((tax) => {
            merged.push({
                id: rowId++,
                _id: tax._id,
                taxName: tax.taxName,
                taxPercentage: String(tax.taxPercentage ?? ""),
                isNew: false,
            });
        });

        setRows(merged);
        setAllRows(merged);
    }, [taxes, employees]);

    // Filter rows based on search query
    const filteredRows = useMemo(() => {
        if (!searchQuery) return rows;

        const query = searchQuery.toLowerCase();
        return rows.filter(
            (row) =>
                row.taxName.toLowerCase().includes(query) ||
                row.taxPercentage.toString().includes(query)
        );
    }, [rows, searchQuery]);

    // Sorting function
    const handleSort = (key) => {
        let direction = "ascending";

        // If already sorted by this key, toggle the direction
        if (sortConfig.key === key && sortConfig.direction === "ascending") {
            direction = "descending";
        }

        setSortConfig({ key, direction });

        const sortedRows = [...rows].sort((a, b) => {
            // Handle empty values by putting them at the end
            if (a[key] === "" || a[key] === null) return 1;
            if (b[key] === "" || b[key] === null) return -1;

            if (a[key] < b[key]) {
                return direction === "ascending" ? -1 : 1;
            }
            if (a[key] > b[key]) {
                return direction === "ascending" ? 1 : -1;
            }
            return 0;
        });

        setRows(sortedRows);
        setAllRows(sortedRows); // Update allRows when sorting
    };

    const handleInputChange = (e, rowId, field) => {
        const value = e.target.value;

        // Validate that taxPercentage is a number
        // if (field === "taxPercentage" && value !== "" && isNaN(value)) {
        //   return;
        // }

        const updatedRows = rows.map((row) =>
            row.id === rowId ? { ...row, [field]: value } : row
        );
        setRows(updatedRows);
        setAllRows(updatedRows);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all rows
        if (rows.some((row) => !row.taxName || !row.taxPercentage)) {
            enqueueSnackbar("Please fill all tax fields", { variant: "error" });
            return;
        }

        try {
            const newTaxes = rows.filter((row) => row.isNew);
            const updatedTaxes = rows.filter((row) => !row.isNew && row._id);

            // Process updates first
            if (updatedTaxes.length > 0) {
                const updatePromises = updatedTaxes.map((tax) => {
                    const payload = {
                        id: tax._id,
                        taxData: {
                            taxName: tax.taxName,
                            taxPercentage: parseFloat(tax.taxPercentage),
                        },
                    };

                    return dispatch(updateSalesTax(payload)).unwrap();
                });

                await Promise.all(updatePromises);
                enqueueSnackbar("G&A Tax Rates Updated Successfully", {
                    variant: "success",
                });
                closeSnackbar();
            }

            // Process new taxes - send as array in one request
            if (newTaxes.length > 0) {
                const taxesArray = newTaxes.map((tax) => ({
                    taxName: tax.taxName,
                    taxPercentage: parseFloat(tax.taxPercentage),
                }));

                await dispatch(createSalesTax({ taxes: taxesArray })).unwrap(); // Wrap in taxes object
                enqueueSnackbar("G&A Tax Rates Created successfully", {
                    variant: "success",
                });
            }

            // Refresh data from backend
            await dispatch(fetchSalesTaxes());
        } catch (error) {
            console.error("Submission error:", error);
            enqueueSnackbar(error.response?.data?.message || "Failed to save taxes", {
                variant: "error",
            });
        }
    };

    // const handleRemoveRow = () => {
    //   if (!selectedrowid) {
    //     enqueueSnackbar("Please Select Row To Delete", { variant: "error" });
    //     return;
    //   }
    //   handleDeleteRow(selectedrowid);
    // };

    const handleDeleteRow = (row) => {
        const id = row?._id;
        if (id) {
            setRowToDelete(id);
            setIsModalOpen(true);
        } else {
            setDefaultRowToRemove(row);
            setIsModal(true);
        }
    };

    const confirmDelete = async () => {
        try {
            // Existing row - delete from backend
            await dispatch(deleteSalesTax(rowid)).unwrap();
            enqueueSnackbar("Row Deleted Successfully", {
                variant: "success",
            });
        } catch (error) {
            console.error("Delete failed:", error);
            enqueueSnackbar(error.message || "Failed to delete tax", {
                variant: "error",
            });
        } finally {
            dispatch(fetchSalesTaxes());
            // setselectedrowid("");
            setIsModalOpen(false);
        }
    };

    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (!query) {
            // If search query is empty, show all rows
            setRows(allRows);
            return;
        }

        // Filter rows based on search query
        const filtered = allRows.filter(
            (row) =>
                row.taxName.toLowerCase().includes(query.toLowerCase()) ||
                row.taxPercentage.toString().includes(query)
        );

        setRows(filtered);
    };

    return (
        <div>
            <div className="pt-6">
                <div className="mr-4 p-4 min-h-[16rem] bg-white rounded-md border border-gray-200 shadow-lg">
                    <div className="flex justify-between px-[2rem] py-1 align-middle items-center">
                        <h1 className="text-xl font-semibold">Add Taxes</h1>

                        <div className="flex gap-2 p-1 align-middle items-center bg-[#e0e7ff] rounded-full">
                            <MdSearch className=" w-5 h-5 text-gray-500" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                placeholder="Search G&A and Tax Rates"
                                className="bg-[#e0e7ff] outline-none w-[80%]"
                            />
                        </div>
                    </div>

                    <div className="w-full text-xs">
                        <div className="w-full">
                            <div className="flex w-full px-[2rem]">
                                <div className="w-1/2 relative bg-[#e8e8e8] font-semibold p-2 ">
                                    Tax
                                    <div className="flex absolute top-0 right-2 flex-col">
                                        <div
                                            className="cursor-pointer"
                                            onClick={() => handleSort("taxName")}
                                        >
                                            <ArrowDropUp
                                                sx={{
                                                    fontSize: "22px",
                                                }}
                                            />
                                        </div>
                                        <div
                                            className="cursor-pointer -mt-2"
                                            onClick={() => handleSort("taxName")}
                                        >
                                            <ArrowDropDown
                                                sx={{
                                                    fontSize: "22px",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="w-1/2 relative bg-[#e8e8e8] font-semibold p-2 ">
                                    {"(%)"}
                                    <div className="flex absolute right-2 top-0 flex-col">
                                        <div
                                            className="cursor-pointer"
                                            onClick={() => handleSort("taxPercentage")}
                                        >
                                            <ArrowDropUp
                                                sx={{
                                                    fontSize: "22px",
                                                    color:
                                                        sortConfig.key === "taxPercentage" &&
                                                            sortConfig.direction === "ascending"
                                                            ? "#ffeb3b"
                                                            : "inherit",
                                                }}
                                            />
                                        </div>
                                        <div
                                            className="cursor-pointer -mt-2"
                                            onClick={() => handleSort("taxPercentage")}
                                        >
                                            <ArrowDropDown
                                                sx={{
                                                    fontSize: "22px",
                                                    color:
                                                        sortConfig.key === "taxPercentage" &&
                                                            sortConfig.direction === "descending"
                                                            ? "#ffeb3b"
                                                            : "inherit",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="w-[5.5%] bg-[#e8e8e8] font-semibold p-2  text-center">
                                    <DeleteIcon />
                                </div>
                            </div>
                        </div>

                        <div className="w-full">
                            <div className="Grouplist w-full">
                                {filteredRows.length > 0 ? (
                                    filteredRows.map((row, index) => (
                                        <div
                                            key={index}
                                            // onClick={() => {
                                            //   setselectedrowid((prevId) =>
                                            //     prevId === row._id ? "" : row._id
                                            //   );
                                            // }}
                                            className="mainrow relative flex w-full px-[2rem] group  transition-colors cursor-pointer"
                                        >
                                            {/* Tax Name Input */}
                                            <div
                                                className="w-1/2 p-1 border-b border-l cursor-default"
                                            >
                                                <input
                                                    type="text"
                                                    value={row.taxName}
                                                    readOnly={true}
                                                    onChange={(e) => {
                                                       
                                                        const validValue = e.target.value.replace(
                                                            /[^a-zA-Z \-_.@#&*$%+=(!)]/g,
                                                            ""
                                                        );

                                                        // Only update if the value is different (to avoid cursor jumping)
                                                        if (validValue !== e.target.value) {
                                                            e.target.value = validValue;
                                                        }
                                                        handleInputChange(e, row.id, "taxName", validValue);
                                                    }}
                                                    placeholder="Enter Tax Name"
                                                    className="w-full p-1 outline-none bg-transparent text-black cursor-default"
                                                    required
                                                />
                                            </div>

                                            {/* Tax Percentage Input */}
                                            <div
                                                className="w-1/2  p-1 border-b"
                                            >
                                                <input
                                                    type="text"
                                                    value={row.taxPercentage}
                                                    // onChange={(e) => {
                                                    //   let cleanedValue = e.target.value;

                                                    //   // ✅ Allow numbers (with up to 3 decimals) + special characters, but block letters
                                                    //   if (
                                                    //     /^[0-9]*\.?[0-9]{0,3}[^a-zA-Z]*$/.test(
                                                    //       cleanedValue
                                                    //     )
                                                    //   ) {
                                                    //     handleInputChange(
                                                    //       {
                                                    //         ...e,
                                                    //         target: { ...e.target, value: cleanedValue },
                                                    //       },
                                                    //       row.id,
                                                    //       "taxPercentage"
                                                    //     );
                                                    //   }
                                                    // }}
                                                    onChange={(e) => {
                                                        let cleanedValue = e.target.value.replace(/-/g, ""); // Remove hyphens

                                                        // ✅ Allow only numbers with up to 3 digits after decimal
                                                        if (/^\d*\.?\d{0,2}$/.test(cleanedValue)) {
                                                            handleInputChange(
                                                                {
                                                                    ...e,
                                                                    target: { ...e.target, value: cleanedValue },
                                                                },
                                                                row.id,
                                                                "taxPercentage"
                                                            );
                                                        }
                                                    }}
                                                    placeholder="Enter Tax Percentage"
                                                    className="w-full p-1 outline-none bg-transparent text-black"
                                                    required
                                                />
                                            </div>

                                            {/* Delete Icon Container - Only shows on hover */}
                                            <div className="w-[5.5%]  border-b border-r p-1  flex items-center justify-center">
                                                <DeleteIcon
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteRow(row);
                                                    }}
                                                    className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-500 cursor-pointer transition-all duration-200"
                                                />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center text-red-600 p-4">
                                        No results found for "{searchQuery}"
                                    </div>
                                )}

                                {/* {!searchQuery && (
                                    <div className="flex justify-center items-center w-full relative mt-2 ">
                                        <div className="absolute left-0 top-0 rounded-md p-1 shadow-lg border flex justify-center items-center bg-white">
                                            <AddIcon
                                                onClick={() => {
                                                    const newRow = {
                                                        id:
                                                            rows.length > 0
                                                                ? Math.max(...rows.map((row) => row.id)) + 1
                                                                : 1,
                                                        taxName: "",
                                                        taxPercentage: "",
                                                        isNew: true, 
                                                    };

                                                    const updatedRows = [...rows, newRow];
                                                    setRows(updatedRows);
                                                    setAllRows(updatedRows); 
                                                }}
                                                className="cursor-pointer text-black"
                                            />
                                        </div>
                                       
                                    </div>
                                )} */}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-10">
                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-[1400] bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
                        <h2 className="text-xl font-semibold mb-4 text-center">
                            Confirm Deletion
                        </h2>
                        <p className="text-gray-700 mb-6 text-center">
                            Are you sure you want to delete this row?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isModal && (
                <div className="fixed inset-0 flex items-center justify-center z-[1400] bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
                        <h2 className="text-xl font-semibold mb-4 text-center">
                            Confirm Deletion
                        </h2>
                        <p className="text-gray-700 mb-6 text-center">
                            Are you sure you want to delete this row?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsModal(false)}
                                className="px-4 py-2 rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (defaultRowToRemove) {
                                        const match = (r) =>
                                            (r.id !== undefined && r.id === defaultRowToRemove.id) ||
                                            (r._id && r._id === defaultRowToRemove._id);
                                        setRows((prev) => prev.filter((r) => !match(r)));
                                        setAllRows((prev) => prev.filter((r) => !match(r)));
                                        setDefaultRowToRemove(null);
                                    }
                                    setIsModal(false);
                                    enqueueSnackbar("Row removed from list.", {
                                        variant: "success",
                                    });
                                }}
                                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )
            }
        </div>
    )
}

export default FinanceTax